import { Canvas as FabricCanvas, Rect, Textbox, FabricImage, Circle, Triangle, Line as FabricLine, Polygon as FabricPolygon, FabricObject, Path as FabricPath, FabricText, Group as FabricGroup, Ellipse, loadSVGFromString, util as fabricUtil } from 'fabric';
import { useCanvasStore } from '../stores/canvasStore';
import { useStudioStore } from '../stores/studioStore';
import { loadCustomFont, isFontReady, isVariantReady, ensureVariantLoaded } from './fontLoader';
import { applyVisualsToFabricObject } from './fabricVisuals';
import { getShapePoints } from './shapePoints';
import { getDashArray } from './elementDefaults';
import { applyTextboxWidthPolicy, applyButtonAdvanceFit, getTextMinWidthFromParams, resolveTextIntent, applyTextTransform, decorationProps, getDescenderSafeHeight, ELEMENT_ID_KEY, ARTBOARD_PADDING } from './textMeasurement';
import { recordButtonSnapshot } from './buttonDiagnosticBuffer';
import type { CanvasElement, TextElement, ShapeElement, ImageElement, ButtonElement, GroupElement, LineElement, PathElement, SvgElement } from '../types';

export function setCrossOriginOnImages(obj: FabricObject): void {
  if (obj instanceof FabricImage) {
    const el = obj.getElement() as HTMLImageElement | null;
    if (el && !el.crossOrigin) el.crossOrigin = 'anonymous';
  }
  if (obj instanceof FabricGroup) {
    for (const child of obj.getObjects()) setCrossOriginOnImages(child);
  }
}

export function resolveTextPathRef(textEl: TextElement, elements: CanvasElement[]): string | null {
  if (!textEl.textPath) return null;
  const pathEl = elements.find((e) => e.id === textEl.textPath!.pathElementId);
  if (!pathEl) {
    console.warn(`[CS] resolveTextPathRef: PathElement ${textEl.textPath.pathElementId} not found (orphan reference on text ${textEl.id})`);
    return null;
  }
  if (pathEl.type !== 'path') {
    console.warn(`[CS] resolveTextPathRef: Element ${textEl.textPath.pathElementId} is type "${pathEl.type}", not path`);
    return null;
  }
  return (pathEl as PathElement).pathData || null;
}

export function createFabricObjectFromElement(
  element: CanvasElement,
  artboardLeft: number,
  artboardTop: number
): FabricObject | null {
  const baseProps = {
    left: artboardLeft + element.x,
    top: artboardTop + element.y,
    angle: element.rotation,
    opacity: element.opacity,
  };

  switch (element.type) {
    case 'text': {
      const textEl = element as TextElement;
      const effectivePathData = resolveTextPathRef(textEl, useStudioStore.getState().canvasState.elements) || textEl.pathData;

      if (effectivePathData) {
        const pathStartOffset = textEl.textPath?.startOffset ?? textEl.pathStartOffset ?? 0;
        const pathSide = textEl.textPath?.side ?? textEl.pathSide ?? 'left';
        const pathAlign = textEl.textPath?.align ?? textEl.pathAlign ?? 'baseline';
        const pathObj = new FabricPath(effectivePathData, {
          fill: '',
          stroke: '',
          visible: false,
        });
        const textOnPath = new FabricText(textEl.text || 'Text on path', {
          ...baseProps,
          fontSize: textEl.fontSize || 24,
          fontFamily: textEl.fontFamily || 'Montserrat',
          fontWeight: String(textEl.fontWeight || 400),
          fontStyle: textEl.fontStyle || 'normal',
          fill: textEl.color || '#ffffff',
          textAlign: textEl.textAlign || 'left',
          path: pathObj,
          pathStartOffset,
          pathSide,
          pathAlign,
          selectable: true,
          evented: true,
          visible: false,
        });
        if (textEl.fontFamily) {
          ensureVariantLoaded(textEl.fontFamily, textEl.fontWeight, textEl.fontStyle)
            .then(() => {
              textOnPath.set({
                fontFamily: textEl.fontFamily,
                visible: textEl.visible !== false,
              });
              if (typeof (textOnPath as any).initDimensions === 'function') {
                (textOnPath as any).initDimensions();
              }
              textOnPath.setCoords();
              textOnPath.canvas?.requestRenderAll();
            })
            .catch((err) => {
              console.warn('[fabricFactory] ensureVariantLoaded failed for text-on-path', textEl.id, err);
              textOnPath.set({ visible: textEl.visible !== false });
              textOnPath.canvas?.requestRenderAll();
            });
        } else {
          textOnPath.set({ visible: textEl.visible !== false });
        }
        return textOnPath;
      }

      // If the element has a saved width, trust it verbatim at creation time. Width
      // validation using `ctx.measureText` against a font that has not yet loaded will
      // use a fallback font with wider glyphs and incorrectly inflate minSafeWidth,
      // leaving dead space on the right once the real font paints (the classic
      // BebasNeuePro "cursor pushed past the last glyph" regression).
      const fontIsReady = isFontReady(textEl.fontFamily || '');
      const savedWidth = textEl.width && textEl.width > 0 ? textEl.width : null;

      let initialWidth: number;
      if (savedWidth != null) {
        initialWidth = savedWidth;
      } else if (fontIsReady) {
        const minWidth = getTextMinWidthFromParams(
          textEl.text || 'Text',
          textEl.fontSize || 24,
          textEl.fontFamily || 'Montserrat',
          textEl.fontWeight || 400,
          textEl.fontStyle || 'normal'
        );
        initialWidth = Math.max(minWidth, (textEl.fontSize || 24) * 2);
      } else {
        initialWidth = (textEl.fontSize || 24) * 6;
      }

      const textbox = new Textbox(applyTextTransform(textEl.text, textEl.textTransform), {
        ...baseProps,
        width: initialWidth,
        fontFamily: textEl.fontFamily,
        fontSize: textEl.fontSize,
        fontWeight: String(textEl.fontWeight),
        fontStyle: textEl.fontStyle,
        textAlign: textEl.textAlign,
        lineHeight: textEl.lineHeight,
        fill: textEl.color,
        charSpacing: textEl.letterSpacing * 10,
        visible: false,
        ...decorationProps(textEl.textDecoration),
      });
      textbox.initDimensions();
      textbox.setCoords();

      const intent = resolveTextIntent(textEl);
      if (fontIsReady) {
        applyTextboxWidthPolicy(textbox, intent);
      }

      loadCustomFont(textEl.fontFamily).then(() => {
        textbox.set({ visible: textEl.visible !== false });

        // Phase B.4: CWT elements have designer-fixed dimensions. Per CWT/CWS
        // Spec Lock §4, width/height MUST NOT be modified by font-load
        // measurement or any non-user-initiated path.
        const isCWT = textEl.textFitMode === 'contain-within-textbox' ||
                      textEl.textFitMode === 'contain-within-structure';

        if (isCWT) {
          if (savedWidth != null) {
            textbox.set({ width: savedWidth });
          }
          if (textEl.height && textEl.height > 0) {
            textbox.set({ height: textEl.height });
          }
          textbox.initDimensions();
          if (savedWidth != null) textbox.set({ width: savedWidth });
          if (textEl.height && textEl.height > 0) textbox.set({ height: textEl.height });
          textbox.setCoords();
          textbox.canvas?.requestRenderAll();
          return;
        }

        textbox.initDimensions();
        if (savedWidth != null) {
          textbox.set({ width: savedWidth });
          textbox.initDimensions();
        }

        if (intent === 'manual') {
          textbox.setCoords();
          textbox.canvas?.requestRenderAll();
          return;
        }

        applyTextboxWidthPolicy(textbox, intent);
        textbox.setCoords();

        const elementId = (textbox as any)[ELEMENT_ID_KEY] as string | undefined;
        if (elementId) {
          const currentEl = useStudioStore
            .getState()
            .canvasState.elements.find((e) => e.id === elementId) as TextElement | undefined;
          if (currentEl) {
            const w = textbox.width ?? 0;
            const h = getDescenderSafeHeight(textbox);
            const widthDiff = Math.abs((currentEl.width ?? 0) - w) > 1;
            const heightDiff = Math.abs((currentEl.height ?? 0) - h) > 1;
            const update: Partial<TextElement> = {};
            if (widthDiff) update.width = w;
            if (heightDiff) update.height = h;
            update.widthIntent = 'manual';
            useStudioStore.getState().updateElement(
              elementId,
              update as Partial<CanvasElement>
            );
          }
        }
        textbox.canvas?.requestRenderAll();
      }).catch((err) => {
        console.warn('[fabricFactory] loadCustomFont failed for text element', textEl.id, err);
        textbox.set({ visible: textEl.visible !== false });
        textbox.canvas?.requestRenderAll();
      });
      return textbox;
    }

    case 'shape': {
      const shapeEl = element as ShapeElement;
      const shapeType = shapeEl.shapeType;
      const shapeVisualOpts = {
        fill: shapeEl.fill,
        stroke: shapeEl.stroke,
        strokeWidth: shapeEl.strokeWidth,
        opacity: shapeEl.opacity * (shapeEl.fillOpacity ?? 1),
      };

      if (shapeType === 'rectangle' || shapeType === 'rounded-rect') {
        return new Rect({
          ...baseProps,
          width: shapeEl.width,
          height: shapeEl.height,
          ...shapeVisualOpts,
          rx: shapeEl.borderRadius ?? 0,
          ry: shapeEl.borderRadius ?? 0,
        });
      }
      if (shapeType === 'circle') {
        return new Circle({
          ...baseProps,
          radius: Math.min(shapeEl.width, shapeEl.height) / 2,
          ...shapeVisualOpts,
        });
      }
      if (shapeType === 'polygon') {
        return new Triangle({
          ...baseProps,
          width: shapeEl.width,
          height: shapeEl.height,
          ...shapeVisualOpts,
        });
      }
      if (shapeType === 'line') {
        return new FabricLine(
          [0, 0, shapeEl.width, 0],
          {
            ...baseProps,
            stroke: shapeEl.stroke !== 'transparent' ? shapeEl.stroke : shapeEl.fill,
            strokeWidth: shapeEl.strokeWidth || 2,
            perPixelTargetFind: true,
            strokeUniform: true,
            padding: Math.max(6, (shapeEl.strokeWidth || 2) * 2),
          }
        );
      }

      const customPoints = getShapePoints(shapeType, shapeEl.width, shapeEl.height);
      if (customPoints) {
        return new FabricPolygon(customPoints, {
          ...baseProps,
          ...shapeVisualOpts,
        });
      }
      return null;
    }

    case 'line': {
      const lineEl = element as LineElement;
      return new FabricLine(
        [
          artboardLeft + lineEl.x + lineEl.x1,
          artboardTop + lineEl.y + lineEl.y1,
          artboardLeft + lineEl.x + lineEl.x2,
          artboardTop + lineEl.y + lineEl.y2,
        ],
        {
          stroke: lineEl.stroke,
          strokeWidth: lineEl.strokeWidth,
          strokeDashArray: getDashArray(lineEl.strokeDashStyle, lineEl.strokeWidth),
          strokeLineCap: lineEl.lineCap,
          opacity: lineEl.opacity,
          angle: lineEl.rotation,
          selectable: !lineEl.locked,
          evented: !lineEl.locked,
          visible: lineEl.visible,
          // Axis-aligned FabricLines have zero thickness on one axis which
          // makes Fabric's default bounding-box hit detection and marquee
          // selection unreliable. Widen the hit region and use per-pixel
          // target-find so shift-click and marquee pick the line up.
          perPixelTargetFind: true,
          strokeUniform: true,
          padding: Math.max(6, (lineEl.strokeWidth || 2) * 2),
          hasBorders: true,
        }
      );
    }

    case 'path': {
      const pathEl = element as PathElement;
      if (!pathEl.pathData) return null;
      const state = useStudioStore.getState();
      const resolvedFill = state.getResolvedElementValue(element, 'fillColor');
      const resolvedStroke = state.getResolvedElementValue(element, 'strokeColor');
      try {
        return new FabricPath(pathEl.pathData, {
          ...baseProps,
          fill: pathEl.closed ? (resolvedFill ?? pathEl.fill ?? 'transparent') : 'transparent',
          stroke: resolvedStroke ?? pathEl.stroke ?? '#ffffff',
          strokeWidth: pathEl.strokeWidth ?? 2,
          strokeDashArray: getDashArray(pathEl.strokeDashStyle || 'solid', pathEl.strokeWidth ?? 2),
          strokeLineCap: pathEl.lineCap || 'round',
          strokeLineJoin: pathEl.lineJoin || 'miter',
          opacity: (pathEl.opacity ?? 1) * (pathEl.closed ? (pathEl.fillOpacity ?? 1) : 1),
        });
      } catch {
        return null;
      }
    }

    case 'image': {
      const imgEl = element as ImageElement;
      const placeholder = new Rect({
        ...baseProps,
        width: imgEl.width,
        height: imgEl.height,
        fill: '#1a1a1a',
        stroke: 'rgba(255,255,255,0.1)',
        strokeWidth: 1,
        strokeDashArray: [4, 4],
      });

      if (imgEl.src) {
        FabricImage.fromURL(imgEl.src, { crossOrigin: 'anonymous' }).then((img) => {
          const canvas = useCanvasStore.getState().fabricCanvas;
          if (!canvas) return;

          const currentEl = useStudioStore.getState().canvasState.elements.find((e) => e.id === imgEl.id);
          const bgVisible = useCanvasStore.getState().showBackgroundLayer;
          const ovVisible = useCanvasStore.getState().showOverlayLayer;
          const elVisible = currentEl
            ? currentEl.visible &&
              ((currentEl.layer === 'BACKGROUND_LAYER' && bgVisible) ||
               (currentEl.layer === 'OVERLAY_LAYER' && ovVisible))
            : true;

          img.set({
            ...baseProps,
            scaleX: imgEl.width / (img.width ?? imgEl.width),
            scaleY: imgEl.height / (img.height ?? imgEl.height),
            visible: elVisible,
            selectable: !(currentEl?.locked) && elVisible,
            evented: !(currentEl?.locked) && elVisible,
          });
          (img as any)[ELEMENT_ID_KEY] = imgEl.id;
          applyVisualsToFabricObject(img, currentEl || imgEl);

          const placeholderIndex = canvas.getObjects().indexOf(placeholder);
          canvas.remove(placeholder);
          canvas.add(img);

          if (placeholderIndex >= 0) {
            canvas.moveObjectTo(img, placeholderIndex);
          } else {
            const elements = useStudioStore.getState().canvasState.elements;
            const sorted = [...elements].sort((a, b) => a.zIndex - b.zIndex);
            const targetIndex = sorted.findIndex((el) => el.id === imgEl.id);
            if (targetIndex >= 0) {
              canvas.moveObjectTo(img, targetIndex + 1);
            }
          }

          canvas.requestRenderAll();
        }).catch(() => {
          placeholder.set({
            fill: '#2a1a1a',
            stroke: 'rgba(239,68,68,0.3)',
          });
          placeholder.canvas?.requestRenderAll();
        });
      }

      return placeholder;
    }

    case 'button': {
      const btnEl = element as ButtonElement;
      const rx = btnEl.borderRadius ?? 8;
      const ry = btnEl.borderRadius ?? 8;

      const state = useStudioStore.getState();
      const resolvedBtnText = state.getResolvedElementValue(element, 'textContent')
        ?? state.getResolvedElementValue(element, 'buttonLabel');
      const resolvedBtnColor = state.getResolvedElementValue(element, 'fontColor');
      const resolvedBtnFill = state.getResolvedElementValue(element, 'buttonFillColor');

      const effectiveBtnFill = resolvedBtnFill ?? btnEl.backgroundColor ?? '#06b6d4';
      const buttonBg = new Rect({
        width: btnEl.width || 180,
        height: btnEl.height || 48,
        fill: effectiveBtnFill,
        rx,
        ry,
        originX: 'center',
        originY: 'center',
      });
      if (effectiveBtnFill === 'transparent') {
        buttonBg.set({
          stroke: 'rgba(6, 182, 212, 0.4)',
          strokeDashArray: [4, 4],
          strokeWidth: 1,
        });
      }

      const buttonLabel = new FabricText(resolvedBtnText ?? btnEl.text ?? 'Click Here', {
        fontSize: btnEl.fontSize || 16,
        fontFamily: btnEl.fontFamily,
        fontWeight: String(btnEl.fontWeight || 600),
        fill: resolvedBtnColor ?? btnEl.color ?? '#ffffff',
        originX: 'center',
        originY: 'center',
        textAlign: 'center',
        visible: false,
      });

      const group = new FabricGroup([buttonBg, buttonLabel], {
        // Phase F.2: FabricGroup has originX/originY:'center', so left/top represent center.
        // element.x/y are stored as top-left (post-migration), so translate to center for Fabric.
        left: (baseProps.left ?? 0) + ((btnEl.width || 180) / 2),
        top: (baseProps.top ?? 0) + ((btnEl.height || 48) / 2),
        angle: baseProps.angle,
        opacity: baseProps.opacity,
        originX: 'center',
        originY: 'center',
      });
      (group as any)._csButtonBg = buttonBg;
      (group as any)._csButtonText = buttonLabel;
      (group as any)._csIsButton = true;

      ensureVariantLoaded(btnEl.fontFamily, btnEl.fontWeight, btnEl.fontStyle)
        .then(() => {
          buttonLabel.set({ fontFamily: btnEl.fontFamily });
          if (typeof (buttonLabel as any).initDimensions === 'function') {
            (buttonLabel as any).initDimensions();
          }
          buttonLabel.set({ visible: true });
          group.setCoords();

          try {
            const labelBounds = buttonLabel.getBoundingRect();
            recordButtonSnapshot({
              timestamp: Date.now(),
              phase: 'factory-font-loaded',
              elementId: btnEl.id || '',
              text: String(buttonLabel.text || ''),
              textLen: String(buttonLabel.text || '').length,
              fontSize: btnEl.fontSize,
              fontFamily: btnEl.fontFamily,
              fontWeight: btnEl.fontWeight,
              paddingX: btnEl.paddingX,
              storedWidth: btnEl.width,
              storedHeight: btnEl.height,
              bgWidth: buttonBg.width,
              bgHeight: buttonBg.height,
              bgLeft: buttonBg.left,
              bgTop: buttonBg.top,
              labelWidth: buttonLabel.width,
              labelHeight: buttonLabel.height,
              labelLeft: buttonLabel.left,
              labelTop: buttonLabel.top,
              labelInkLeft: labelBounds.left,
              labelInkRight: labelBounds.left + labelBounds.width,
              groupLeft: group.left,
              groupTop: group.top,
              groupWidth: group.width,
              groupHeight: group.height,
              fitChanged: undefined,
              fitNewWidth: undefined,
              note: 'render path; no width mutation',
            });
          } catch { /* diagnostic only */ }

          group.canvas?.requestRenderAll();
        })
        .catch((err) => {
          console.warn('[fabricFactory] ensureVariantLoaded failed for button', btnEl.id, err);
          buttonLabel.set({ visible: true });
          group.canvas?.requestRenderAll();
        });

      return group;
    }

    case 'group': {
      const groupEl = element as GroupElement;
      // DIAGNOSTIC — temporary
      console.log('[GROUP-DEBUG-FACTORY]', {
        stage: 'fabricFactory-group-create',
        elementId: groupEl.id,
        groupElStoreCoords: {
          x: groupEl.x,
          y: groupEl.y,
          width: groupEl.width,
          height: groupEl.height,
        },
        fabricArtboardOffset: { artboardLeft, artboardTop },
        computedFabricPosition: {
          left: artboardLeft + groupEl.x,
          top: artboardTop + groupEl.y,
        },
        baseProps,
      });
      // END DIAGNOSTIC
      return new Rect({
        ...baseProps,
        width: groupEl.width,
        height: groupEl.height,
        fill: 'transparent',
        stroke: 'rgba(6, 182, 212, 0.3)',
        strokeWidth: 1,
        strokeDashArray: [6, 3],
      });
    }

    case 'svg': {
      const svgEl = element as SvgElement;
      const placeholder = new Rect({
        ...baseProps,
        width: svgEl.width,
        height: svgEl.height,
        fill: '#1a1a1a',
        stroke: 'rgba(168,85,247,0.2)',
        strokeWidth: 1,
        strokeDashArray: [4, 4],
      });

      loadSVGFromString(svgEl.svgMarkup).then(({ objects, options }) => {
        const canvas = useCanvasStore.getState().fabricCanvas;
        if (!canvas) return;

        const validObjects = objects.filter((obj): obj is FabricObject => obj !== null);
        if (validObjects.length === 0) {
          placeholder.set({
            fill: '#2a1a1a',
            stroke: 'rgba(239,68,68,0.3)',
          });
          (placeholder as any)._csSvgError = true;
          placeholder.canvas?.requestRenderAll();
          return;
        }

        const group = fabricUtil.groupSVGElements(validObjects, options);
        setCrossOriginOnImages(group);
        const currentEl = useStudioStore.getState().canvasState.elements.find((e) => e.id === svgEl.id);
        const bgVisible = useCanvasStore.getState().showBackgroundLayer;
        const ovVisible = useCanvasStore.getState().showOverlayLayer;
        const elVisible = currentEl
          ? currentEl.visible &&
            ((currentEl.layer === 'BACKGROUND_LAYER' && bgVisible) ||
             (currentEl.layer === 'OVERLAY_LAYER' && ovVisible))
          : true;

        group.set({
          left: artboardLeft + svgEl.x,
          top: artboardTop + svgEl.y,
          scaleX: svgEl.width / (svgEl.originalWidth || 1),
          scaleY: svgEl.height / (svgEl.originalHeight || 1),
          angle: svgEl.rotation || 0,
          opacity: svgEl.opacity ?? 1,
          selectable: !(currentEl?.locked) && elVisible,
          evented: !(currentEl?.locked) && elVisible,
          visible: elVisible,
          lockUniScaling: svgEl.preserveAspectRatio,
        });
        (group as any)[ELEMENT_ID_KEY] = svgEl.id;
        (group as any)._csIsSvg = true;
        (group as any)._csSvgMarkup = svgEl.svgMarkup;

        const placeholderIndex = canvas.getObjects().indexOf(placeholder);
        canvas.remove(placeholder);
        canvas.add(group);

        if (placeholderIndex >= 0) {
          canvas.moveObjectTo(group, placeholderIndex);
        } else {
          const elements = useStudioStore.getState().canvasState.elements;
          const sorted = [...elements].sort((a, b) => a.zIndex - b.zIndex);
          const targetIndex = sorted.findIndex((el) => el.id === svgEl.id);
          if (targetIndex >= 0) {
            canvas.moveObjectTo(group, targetIndex + 1);
          }
        }

        canvas.requestRenderAll();
      }).catch(() => {
        placeholder.set({
          fill: '#2a1a1a',
          stroke: 'rgba(239,68,68,0.3)',
        });
        (placeholder as any)._csSvgError = true;
        placeholder.canvas?.requestRenderAll();
      });

      return placeholder;
    }

    default:
      return null;
  }
}
